import { Link } from 'react-router-dom';
import SEO from '../../../components/base/SEO';

export default function HermeticChimneyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Hermetic Chimney Systems',
    description: 'Advanced hermetic chimney systems for condensing and non-condensing heating devices. Ensures safe air intake and exhaust discharge for optimal performance.',
    brand: {
      '@type': 'Brand',
      name: 'Proform Chimney Systems'
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'Proform Chimney Systems',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Akçaburgaz Mah. 3028 Sk. No:1 İç Kapı No:27',
        addressLocality: 'Esenyurt',
        addressRegion: 'Istanbul',
        addressCountry: 'TR'
      }
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'TRY',
      url: `${siteUrl}/services/hermetic`
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '84'
    }
  };

  const technicalSpecs = [
    { label: 'System Type', value: 'Hermetic Double-Walled' },
    { label: 'Fuel Compatibility', value: 'Natural Gas / Liquid Gas' },
    { label: 'Device Type', value: 'Condensing / Non-Condensing' },
    { label: 'Air Intake Method', value: 'Through Wall Space' },
    { label: 'Exhaust Method', value: 'Through Inner Channel' },
    { label: 'Construction', value: 'Modular Design' },
    { label: 'Material', value: 'Stainless Steel' },
    { label: 'Application', value: 'Residential & Commercial' }
  ];

  const images = [
    {
      url: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/7c7c16c4859bfb1f98160792a2b2369e.jpeg',
      title: 'Hermetic System Overview'
    },
    {
      url: 'https://public.readdy.ai/ai/img_res/c9fc71a00ab0dae47b122185286a681d.jpg',
      title: 'Double-Wall Construction'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Close-up%20of%20hermetic%20chimney%20system%20components%20and%20connections%20showing%20modular%20design%20precision%20engineering%20stainless%20steel%20finish%20product%20photography%20simple%20background&width=800&height=600&seq=hermetic-2&orientation=landscape',
      title: 'System Components'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Hermetic%20chimney%20installation%20on%20building%20exterior%20showing%20complete%20system%20from%20heating%20device%20to%20roof%20discharge%20professional%20photography%20architectural%20view&width=800&height=600&seq=hermetic-3&orientation=landscape',
      title: 'Complete Installation'
    }
  ];

  return (
    <>
      <SEO
        title="Hermetic Chimney Systems Turkey - Advanced Gas Heating Solutions | Proform"
        description="Advanced hermetic chimney systems for natural gas and liquid gas heating. Double-walled design with air intake and exhaust discharge. Expert installation in Turkey."
        keywords="hermetic chimney, gas chimney Turkey, condensing chimney, natural gas chimney, heating system, air intake chimney"
        canonical={`${siteUrl}/services/hermetic`}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://proformbaca.com/wp-content/uploads/2024/02/4-1.jpg"
              alt="Hermetic Chimney Systems"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/95 via-[#1e40af]/90 to-[#1e3a8a]/95"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center text-white">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="hover:text-orange-400 transition-colors">Home</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li>
                  <Link to="/services" className="hover:text-orange-400 transition-colors">Services</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-orange-400 font-semibold">Hermetic</li>
              </ol>
            </nav>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">Hermetic Chimney Systems</h1>
            <p className="text-2xl md:text-3xl max-w-3xl leading-relaxed">
              Advanced systems for safe air intake and exhaust discharge
            </p>
          </div>
        </section>

        {/* Description Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-3xl shadow-2xl p-12 mb-12">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-2xl flex items-center justify-center shadow-lg">
                  <i className="ri-information-line text-3xl text-white"></i>
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Product Description</h2>
              </div>
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  Our hermetic chimney system is designed for the safe discharge of exhaust gases from condensing or non-condensing heating devices that operate with natural gas or liquid gas.
                </p>
                <p>
                  The system's products consist of various modules with a double-walled construction, without insulation material for filling.
                </p>
                <p>
                  The hermetic chimney system ensures the intake of combustion air required for the connected device through the space between the outer and inner walls.
                </p>
                <p>
                  Similarly, it facilitates the safe discharge of post-combustion exhaust gases through the interior of the chimney, ensuring the secure operation of the system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section - Step by Step Visual Flow */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
              <p className="text-xl text-gray-600">Understanding the hermetic chimney system operation</p>
            </div>
            
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#1e3a8a] via-orange-500 to-[#1e3a8a] transform -translate-y-1/2"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
                {/* Step 1 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-blue-200">
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-full flex items-center justify-center shadow-2xl">
                        <span className="text-4xl font-bold text-white">1</span>
                      </div>
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <i className="ri-wind-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Air Intake</h3>
                    <p className="text-gray-700 leading-relaxed text-center">
                      Fresh combustion air is drawn in through the space between the outer and inner walls of the hermetic system
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-orange-200">
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-2xl">
                        <span className="text-4xl font-bold text-white">2</span>
                      </div>
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <i className="ri-fire-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Combustion Process</h3>
                    <p className="text-gray-700 leading-relaxed text-center">
                      The heating device uses the air for efficient combustion of natural or liquid gas fuel
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-teal-200">
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-2xl">
                        <span className="text-4xl font-bold text-white">3</span>
                      </div>
                    </div>
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <i className="ri-arrow-up-circle-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Exhaust Discharge</h3>
                    <p className="text-gray-700 leading-relaxed text-center">
                      Post-combustion gases are safely discharged through the interior chimney channel to the atmosphere
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Gallery - 2 Column Grid */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Product Gallery</h2>
              <p className="text-xl text-gray-600">Explore our hermetic chimney systems in detail</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {images.map((image, index) => (
                <div key={index} className="relative group overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-96 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-bold">{image.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Specifications Table */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
              <p className="text-xl text-gray-600">Detailed specifications of our hermetic systems</p>
            </div>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af]">
                      <th className="px-8 py-6 text-left text-xl font-bold text-white">Specification</th>
                      <th className="px-8 py-6 text-left text-xl font-bold text-white">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {technicalSpecs.map((spec, index) => (
                      <tr key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-orange-50 transition-colors`}>
                        <td className="px-8 py-5 text-gray-900 font-semibold text-lg border-b border-gray-200">{spec.label}</td>
                        <td className="px-8 py-5 text-gray-700 text-lg border-b border-gray-200">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Key Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-blue-200">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-gas-station-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Gas Compatibility</h3>
                <p className="text-gray-700 leading-relaxed">Designed for natural gas and liquid gas heating devices</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-orange-200">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-stack-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Double-Walled Design</h3>
                <p className="text-gray-700 leading-relaxed">Modular construction with double walls for optimal performance</p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-teal-200">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-wind-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Air Intake System</h3>
                <p className="text-gray-700 leading-relaxed">Combustion air intake through the space between walls</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-purple-200">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-arrow-up-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Safe Exhaust Discharge</h3>
                <p className="text-gray-700 leading-relaxed">Secure discharge of post-combustion exhaust gases through the interior</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-green-200">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-temp-cold-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Condensing Compatible</h3>
                <p className="text-gray-700 leading-relaxed">Suitable for both condensing and non-condensing heating devices</p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-red-200">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Secure Operation</h3>
                <p className="text-gray-700 leading-relaxed">Ensures safe and reliable system operation at all times</p>
              </div>
            </div>
          </div>
        </section>

        {/* Prominent CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 text-center">
            <div className="mb-8">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-white font-semibold mb-6">
                <i className="ri-phone-line text-xl"></i>
                <span>Get in Touch Today</span>
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Ready to Get Started?</h2>
            <p className="text-2xl text-white/95 mb-12 leading-relaxed max-w-3xl mx-auto">
              Contact us today to discuss your hermetic chimney system requirements and receive a free consultation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-white text-orange-500 px-10 py-5 rounded-full font-bold text-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-mail-line text-2xl"></i>
                <span>Contact Us</span>
              </Link>
              <a
                href="https://wa.me/905326304227"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-[#20BA5A] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}